import React, { useMemo } from "react";
import { Canvas, useThree, ThreeEvent, useFrame } from "@react-three/fiber";
import { shaderMaterial, useTrailTexture, Text } from "@react-three/drei";
import * as THREE from "three";
import { useSliderBlade, useTweakpane } from "react-tweakpane";
import { EffectComposer, Bloom } from "@react-three/postprocessing";

interface DotMaterialProps {
  resolution: THREE.Vector2;
  mouseTrail: THREE.Texture | null;
  gridSize: number;
}

// Extend THREE.ShaderMaterial to include our custom uniforms
interface CustomShaderMaterial extends THREE.ShaderMaterial {
  resolution: THREE.Vector2;
  mouseTrail: THREE.Texture | null;
  gridSize: number;
}

const DotMaterial = shaderMaterial(
  {
    resolution: new THREE.Vector2(),
    mouseTrail: null,
    gridSize: 40,
    time: 0,
  },
  `
    varying vec2 vUv;
    void main() {
      gl_Position = vec4(position.xy, 0.0, 1.0);
    }
  `,
  `
    uniform vec2 resolution;
uniform sampler2D mouseTrail;
uniform float gridSize;
uniform float time; // Add time uniform for animation

vec3 colorA = vec3(3.0, 3.0, 0.0);  // Red
vec3 colorB = vec3(0.0, 3.0, 3.0);  // Green
vec3 colorC = vec3(3.0, 0.0, 3.0);  // Blue

vec2 coverUv(vec2 uv) {
  vec2 s = resolution.xy / max(resolution.x, resolution.y);
  vec2 newUv = (uv - 0.5) * s + 0.5;
  return clamp(newUv, 0.0, 1.0);
}

/* SDF Shapes */
float sdfCircle(vec2 p, float r) {
  return length(p - 0.5) - r;
}

// Function to create smooth color transitions
vec3 getGradientColor(float t) {
    float normalizedTime = fract(t * 0.1); // Slow down the color change
    
    // Create a 3-way gradient
    if(normalizedTime < 0.33) {
        return mix(colorA, colorB, normalizedTime * 3.0);
    } else if(normalizedTime < 0.66) {
        return mix(colorB, colorC, (normalizedTime - 0.33) * 3.0);
    } else {
        return mix(colorC, colorA, (normalizedTime - 0.66) * 3.0);
    }
}

void main() {
  float aspect = resolution.x / resolution.y;
  vec2 screenUv = gl_FragCoord.xy / resolution;
  vec2 uv = coverUv(screenUv);
  
  // Create a grid
  vec2 gridUv = fract(uv * gridSize);
  vec2 gridUvCenter = (floor(uv * gridSize) + 0.5) / gridSize;
  
  // Sample mouse trail
  float trail = texture2D(mouseTrail, gridUvCenter).r;
  
  // Get the time-based gradient color
  vec3 gradientColor = getGradientColor(time);
  
  // Mix the trail intensity with the gradient color
  vec3 finalColor = gradientColor * trail;
  
  gl_FragColor = vec4(finalColor, 1.0);
}
  `
) as new () => object;

interface SceneProps {}

function Scene({}: SceneProps) {
  const size = useThree((s) => s.size);
  const viewport = useThree((s) => s.viewport);
//   const pane = useTweakpane();

  const dotMaterial = useMemo(() => {
    return new DotMaterial();
  }, []);
  
  useFrame((state) => {
    if (dotMaterial && 'uniforms' in dotMaterial) {
      (dotMaterial as any).uniforms.time.value = state.clock.elapsedTime;
    }
  });

//   const [gridSize] = useSliderBlade(pane, {
//     label: "Cells",
//     min: 10,
//     max: 1000,
//     value: 1000,
//   });

  const [trail, onMove] = useTrailTexture({
    size: 1028,
    radius: 0.05,
    maxAge: 3000,
    interpolate: 2,
    ease: function easeInOutCirc(x: number): number {
      return x < 0.5
        ? (1 - Math.sqrt(1 - Math.pow(2 * x, 2))) / 5.0
        : (Math.sqrt(1 - Math.pow(-2 * x + 2, 2)) + 3.0) / 5;
    },
  });

  const scale = Math.max(viewport.width, viewport.height) / 2;

  const handlePointerMove = (event: ThreeEvent<PointerEvent>) => {
    onMove(event);
  };

  return (<>
  <EffectComposer>
  <Bloom mipmapBlur intensity={0.2} /></EffectComposer>
    <mesh scale={[scale, scale, 1]} onPointerMove={handlePointerMove}>
      <planeGeometry args={[2, 2]} />
      <primitive
        object={dotMaterial}
        gridSize={1000}
        resolution={[size.width * viewport.dpr, size.height * viewport.dpr]}
        mouseTrail={trail}
      />
    </mesh>
   
    </>
  );
}

interface CodropsDemoProps {}

export default function CodropsDemo({}: CodropsDemoProps): JSX.Element {
  return (
    <div className="w-full h-full z-0">
    <Canvas
      gl={{
        antialias: true,
        // powerPreference: "high-performance",
      }}
    >
      <Scene />
    </Canvas></div>
  );
}
